import type { Command } from "@shared-core/domain/object-ui/contract";
import type { PresentedGridGroup } from "@shared-core/domain/object-ui/record";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../primitives/table";
import { RowCommands } from "./RowCommands";

/**
 * A child's rows as grids (`children[].grid`, `presentChildGrid`): one small table for each group, a row for each
 * thing compared and a column for each value compared on, the value where they cross, the cell's own text beneath
 * it (or in its place where it has no value) and its commands under that. The mark ("Lowest") and the text come from
 * the rows; this only draws them. A phone scrolls the grid sideways rather than losing a column.
 */
export function ChildGrid({
  groups,
  rowHeading,
  isPhone,
  declaredMainOf,
  onRun,
}: {
  groups: PresentedGridGroup[];
  /** What the first column holds ("Supplier"), for a screen reader. */
  rowHeading: string;
  isPhone: boolean;
  /** The command the cell's object declares as its main one, among these. */
  declaredMainOf: (commands: Command[]) => string | null;
  /** Runs a cell's command, its dialog headed by the row's command heading. */
  onRun: (command: Command, commandHeading: string) => void;
}) {
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.key} className="space-y-1">
          {group.heading && (
            <h3 className="text-sm font-semibold tabular-nums">
              {group.heading}
            </h3>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <span className="sr-only">{rowHeading}</span>
                </TableHead>
                {group.columns.map((column) => (
                  <TableHead key={column.key} className="tabular-nums">
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableHead scope="row" className="font-medium">
                    {row.label}
                  </TableHead>
                  {row.cells.map((cell, n) => (
                    <TableCell
                      key={group.columns[n]!.key}
                      className="align-top"
                    >
                      {cell && (
                        <div className="flex flex-col items-start gap-1">
                          {cell.text && (
                            <span
                              className={
                                cell.isHighlighted
                                  ? "font-semibold tabular-nums"
                                  : "tabular-nums"
                              }
                            >
                              {cell.text}
                              {cell.isHighlighted && (
                                <span className="ml-1.5 rounded border border-border px-1 py-0.5 align-middle text-xs font-normal text-muted-foreground">
                                  Lowest
                                </span>
                              )}
                            </span>
                          )}
                          {cell.state && (
                            <span className="text-xs text-muted-foreground">
                              {cell.state}
                            </span>
                          )}
                          {cell.commands.length > 0 && (
                            <RowCommands
                              commands={cell.commands}
                              declaredMain={declaredMainOf(cell.commands)}
                              isPhone={isPhone}
                              onRun={(command) =>
                                onRun(command, cell.commandHeading)
                              }
                            />
                          )}
                        </div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}
